import type { FirestoreTimestamp } from './firestore'
import type { EmailCategory } from './email'

export type NotificationType =
  | 'group_invite'
  | 'friend_request'
  | 'set_post'
  | 'event_linked'
  | 'schedule_update'
  | 'event_suggestion'
  | 'artist_match'
  | 'request_addressed'
  | 'event_cancelled'
  | 'ticket_update'

/** Which kind of user request a `request_addressed` push is about — rides in `data.requestType`. */
export type RequestAddressedType = 'schedule_lead' | 'feedback' | 'region_request'

/** `sending` = claimed by a delivery run; a stale claim is re-claimed after STALE_CLAIM_MS. */
export type NotificationStatus = 'pending' | 'sending' | 'sent' | 'failed' | 'skipped'

/**
 * Why a notification ended up `skipped` — `skipped` alone is ambiguous.
 * `stale_backlog` is historical: a one-off retirement of the queue that piled
 * up while processDelayedNotifications was failing on a missing index.
 */
export type NotificationSkipReason = 'opted_out' | 'no_tokens' | 'stale_backlog'

export interface NotificationQueueItem {
  id: string
  type: NotificationType
  recipientId: string
  title: string
  body: string
  deepLink: string
  data?: Record<string, string>
  status: NotificationStatus
  skipReason?: NotificationSkipReason
  createdAt: FirestoreTimestamp
  sentAt?: FirestoreTimestamp
  claimedAt?: FirestoreTimestamp
  sourceType: string
  sourceId: string
  sendAfter?: FirestoreTimestamp
  /** schedule_update digests only: the sets the pending push covers. */
  changedSetIds?: string[]
}

export interface NotificationPreferences {
  enabled: boolean
  categories: Record<NotificationType, boolean>
  /**
   * Milestone emails, one switch per category; missing = on. Independent of
   * the push master `enabled`. Read through `emailCategoryEnabled`.
   */
  emailCategories?: Partial<Record<EmailCategory, boolean>>
}

export const DEFAULT_NOTIFICATION_PREFERENCES: NotificationPreferences = {
  enabled: true,
  categories: {
    group_invite: true,
    friend_request: true,
    set_post: true,
    event_linked: true,
    schedule_update: true,
    event_suggestion: true,
    artist_match: true,
    request_addressed: true,
    event_cancelled: true,
    ticket_update: true,
  },
}

export interface FcmTokenEntry {
  platform: 'ios' | 'android' | 'web'
  createdAt: FirestoreTimestamp
  lastUsedAt: FirestoreTimestamp
}

export interface FcmTokenDoc {
  tokens: Record<string, FcmTokenEntry>
}
